import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Radar } from 'react-chartjs-2';
import { X, Edit2, Trash2, CheckCircle, XCircle } from 'lucide-react';
import InfoTooltip from '../components/InfoTooltip';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Tooltip,
  Legend,
  Filler
);

// Dummy data for sparkline (last 6 submissions)
const marks = [70, 75, 73, 80, 78, 85];
const sparklineData = {
  labels: marks.map((_, i) => `Sub ${i + 1}`),
  datasets: [
    {
      label: 'Marks',
      data: marks,
      borderColor: '#14B8A6', // teal-500
      backgroundColor: 'transparent',
      pointRadius: 4,
      pointBackgroundColor: marks.map((val, idx) =>
        idx > 0 && val < marks[idx - 1] ? 'rgba(100,116,139,0.5)' : '#14B8A6'
      ),
      tension: 0.4,
      fill: false,
    },
  ],
};
const sparklineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { display: false },
    y: { display: false },
  },
};

// Dummy data for radar chart (strength flower)
const radarData = {
  labels: ['Engagement', 'Collaboration', 'Self-regulation', 'Reflection'],
  datasets: [
    {
      label: 'Strengths',
      data: [78, 60, 82, 70],
      backgroundColor: 'rgba(167,243,208,0.5)', // teal-200
      borderColor: '#14B8A6',
      borderWidth: 2,
      pointBackgroundColor: '#14B8A6',
    },
  ],
};
const radarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    r: {
      min: 0,
      max: 100,
      ticks: { display: false },
      grid: { color: 'rgba(203,213,225,0.5)' },
      angleLines: { color: 'rgba(203,213,225,0.5)' },
      pointLabels: { color: '#4B5563', font: { size: 14 } },
    },
  },
};

// Dummy weekly engagement intensity (1–5 scale)
const heatData = [1, 3, 4, 2, 3, 2, 5, 4];
const getHeatColor = (val: number) => {
  if (val <= 1) return 'bg-slate-200';
  if (val <= 2) return 'bg-amber-200';
  if (val <= 3) return 'bg-amber-400';
  if (val <= 4) return 'bg-amber-600';
  return 'bg-amber-800';
};

// Dummy achievements for positive feed
const initialAchievements = [
  { id: 1, emoji: '👏', text: 'You joined 3 study groups' },
  { id: 2, emoji: '🎉', text: 'Finished first draft early' },
  { id: 3, emoji: '💬', text: 'Posted 2 comments this week' },
];

export default function StudentDetailPage() {
  const [achievements, setAchievements] = useState(initialAchievements);
  const [viewMode, setViewMode] = useState<'teacher' | 'student'>('teacher');

  const removeAchievement = (id: number) => {
    setAchievements((prev) => prev.filter((a) => a.id !== id));
  };

  // Notes state and handlers
  const [courseNotesList, setCourseNotesList] = useState([
    { id: 1, text: 'Syllabus updated: see new guidelines.' },
    { id: 2, text: 'Next group project draft due Week 5.' },
  ]);
  const [editingCourseNoteId, setEditingCourseNoteId] = useState<number | null>(null);
  const [editingCourseText, setEditingCourseText] = useState('');
  const [newCourseNote, setNewCourseNote] = useState('');
  const addCourseNote = () => {
    if (!newCourseNote.trim()) return;
    setCourseNotesList((prev) => [
      ...prev,
      { id: prev.length + 1, text: newCourseNote.trim() },
    ]);
    setNewCourseNote('');
  };
  const saveCourseNote = () => {
    if (editingCourseNoteId === null) return;
    setCourseNotesList((prev) =>
      prev.map((note) =>
        note.id === editingCourseNoteId ? { ...note, text: editingCourseText } : note
      )
    );
    setEditingCourseNoteId(null);
    setEditingCourseText('');
  };
  const deleteCourseNote = (id: number) => {
    setCourseNotesList((prev) => prev.filter((note) => note.id !== id));
  };
  const editCourseNote = (id: number) => {
    const note = courseNotesList.find((n) => n.id === id);
    if (!note) return;
    setEditingCourseNoteId(id);
    setEditingCourseText(note.text);
  };

  const [studentNotesList, setStudentNotesList] = useState([
    { id: 1, text: 'I found the last lecture helpful.' },
  ]);
  const [editingStudentNoteId, setEditingStudentNoteId] = useState<number | null>(null);
  const [editingStudentText, setEditingStudentText] = useState('');
  const [newStudentNote, setNewStudentNote] = useState('');
  const addStudentNote = () => {
    if (!newStudentNote.trim()) return;
    setStudentNotesList((prev) => [
      ...prev,
      { id: prev.length + 1, text: newStudentNote.trim() },
    ]);
    setNewStudentNote('');
  };
  const saveStudentNote = () => {
    if (editingStudentNoteId === null) return;
    setStudentNotesList((prev) =>
      prev.map((note) =>
        note.id === editingStudentNoteId ? { ...note, text: editingStudentText } : note
      )
    );
    setEditingStudentNoteId(null);
    setEditingStudentText('');
  };
  const deleteStudentNote = (id: number) => {
    setStudentNotesList((prev) => prev.filter((note) => note.id !== id));
  };
  const editStudentNote = (id: number) => {
    const note = studentNotesList.find((n) => n.id === id);
    if (!note) return;
    setEditingStudentNoteId(id);
    setEditingStudentText(note.text);
  };

  const [privateNotesList, setPrivateNotesList] = useState([
    { id: 1, text: 'Prefers one-on-one feedback.' },
  ]);
  const [editingPrivateNoteId, setEditingPrivateNoteId] = useState<number | null>(null);
  const [editingPrivateText, setEditingPrivateText] = useState('');
  const [newPrivateNote, setNewPrivateNote] = useState('');
  const addPrivateNote = () => {
    if (!newPrivateNote.trim()) return;
    setPrivateNotesList((prev) => [
      ...prev,
      { id: prev.length + 1, text: newPrivateNote.trim() },
    ]);
    setNewPrivateNote('');
  };
  const savePrivateNote = () => {
    if (editingPrivateNoteId === null) return;
    setPrivateNotesList((prev) =>
      prev.map((note) =>
        note.id === editingPrivateNoteId ? { ...note, text: editingPrivateText } : note
      )
    );
    setEditingPrivateNoteId(null);
    setEditingPrivateText('');
  };
  const deletePrivateNote = (id: number) => {
    setPrivateNotesList((prev) => prev.filter((note) => note.id !== id));
  };
  const editPrivateNote = (id: number) => {
    const note = privateNotesList.find((n) => n.id === id);
    if (!note) return;
    setEditingPrivateNoteId(id);
    setEditingPrivateText(note.text);
  };

  const [classroomNotesList, setClassroomNotesList] = useState([
    { id: 1, text: 'Sits near Alex; keep distraction in check.' },
    { id: 2, text: 'Needs more encouragement during labs.' },
  ]);
  const [editingClassroomNoteId, setEditingClassroomNoteId] = useState<number | null>(null);
  const [editingClassroomText, setEditingClassroomText] = useState('');
  const [newClassroomNote, setNewClassroomNote] = useState('');
  const addClassroomNote = () => {
    if (!newClassroomNote.trim()) return;
    setClassroomNotesList((prev) => [
      ...prev,
      { id: prev.length + 1, text: newClassroomNote.trim() },
    ]);
    setNewClassroomNote('');
  };
  const saveClassroomNote = () => {
    if (editingClassroomNoteId === null) return;
    setClassroomNotesList((prev) =>
      prev.map((note) =>
        note.id === editingClassroomNoteId ? { ...note, text: editingClassroomText } : note
      )
    );
    setEditingClassroomNoteId(null);
    setEditingClassroomText('');
  };
  const deleteClassroomNote = (id: number) => {
    setClassroomNotesList((prev) => prev.filter((note) => note.id !== id));
  };
  const editClassroomNote = (id: number) => {
    const note = classroomNotesList.find((n) => n.id === id);
    if (!note) return;
    setEditingClassroomNoteId(id);
    setEditingClassroomText(note.text);
  };
  // Dummy attendance data (last 7 sessions)
  const attendanceRecords = [
    { id: 1, day: 'Mon', present: true },
    { id: 2, day: 'Tue', present: true },
    { id: 3, day: 'Wed', present: false },
    { id: 4, day: 'Thu', present: true },
    { id: 5, day: 'Fri', present: true },
    { id: 6, day: 'Mon', present: true },
    { id: 7, day: 'Tue', present: false },
  ];
  // Dummy outstanding assignments
  const outstandingAssignments = [
    { id: 1, title: 'Essay on WW2', course: 'History 101', due: '2025-06-12' },
    { id: 2, title: 'Lab Report', course: 'Chemistry 201', due: '2025-06-14' },
    { id: 3, title: 'Problem Set 5', course: 'Math 301', due: '2025-06-15' },
  ];

  return (
    <div className="p-8 space-y-8">
      {/* Header with view toggle */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Strength Dashboard</h1>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Teacher View</span>
          <button
            onClick={() => setViewMode(viewMode === 'teacher' ? 'student' : 'teacher')}
            className="relative inline-flex items-center w-10 h-6 bg-gray-200 rounded-full focus:outline-none"
            aria-label="Toggle view mode"
          >
            <span
              className={`bg-white w-4 h-4 rounded-full shadow transform transition ${
                viewMode === 'student' ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <span className="text-sm text-gray-600">Student View</span>
        </div>
      </div>

      {/* Teacher panels: Attendance & Outstanding Assignments */}
      {viewMode === 'teacher' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Attendance */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Attendance (Last 7 Sessions)</h2>
            <div className="flex space-x-4">
              {attendanceRecords.map((r) => (
                <div key={r.id} className="flex flex-col items-center">
                  <span className="text-sm text-gray-500">{r.day}</span>
                  {r.present ? (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500" />
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Outstanding Assignments */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Outstanding Assignments</h2>
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {outstandingAssignments.map((a) => (
                <li key={a.id} className="flex justify-between items-center p-2 bg-surface-muted rounded">
                  <div>
                    <p className="font-medium text-gray-700">{a.title}</p>
                    <p className="text-xs text-gray-500">{a.course}</p>
                  </div>
                  <p className="text-sm text-red-600">Due {a.due}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Notes Sections */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Notes</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Course Notes */}
          {viewMode === 'teacher' && (
            <div className="bg-white p-4 rounded-lg shadow group">
              <h3 className="text-lg font-medium mb-2">Course Notes</h3>
              <div className="space-y-2 blur-sm group-hover:blur-none transition-all">
                {courseNotesList.map((note) => (
                  <div key={note.id} className="flex items-start justify-between">
                    {editingCourseNoteId === note.id ? (
                      <div className="flex-1 space-y-2">
                        <textarea
                          value={editingCourseText}
                          onChange={(e) => setEditingCourseText(e.target.value)}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={saveCourseNote}
                            className="px-3 py-1 bg-primary text-white rounded text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCourseNoteId(null)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700">{note.text}</p>
                        <div className="flex space-x-2">
                          <button onClick={() => editCourseNote(note.id)} className="text-gray-500 hover:text-gray-700">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => deleteCourseNote(note.id)} className="text-gray-500 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <textarea
                  value={newCourseNote}
                  onChange={(e) => setNewCourseNote(e.target.value)}
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="New course note..."
                />
                <button
                  onClick={addCourseNote}
                  className="mt-2 px-3 py-1 bg-primary text-white rounded text-sm"
                >
                  Add Note
                </button>
              </div>
            </div>
          )}
          {/* Student Notes */}
          <div className="bg-white p-4 rounded-lg shadow group">
          <h3 className="text-lg font-medium mb-2">Student Notes</h3>
            <div className="space-y-2 blur-sm group-hover:blur-none transition-all">
              {studentNotesList.map((note) => (
                <div key={note.id} className="flex items-start justify-between">
                  {editingStudentNoteId === note.id ? (
                    <div className="flex-1 space-y-2">
                      <textarea
                        value={editingStudentText}
                        onChange={(e) => setEditingStudentText(e.target.value)}
                        rows={2}
                        className="w-full p-2 border border-gray-300 rounded"
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={saveStudentNote}
                          className="px-3 py-1 bg-primary text-white rounded text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingStudentNoteId(null)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700">{note.text}</p>
                      <div className="flex space-x-2">
                        <button onClick={() => editStudentNote(note.id)} className="text-gray-500 hover:text-gray-700">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => deleteStudentNote(note.id)} className="text-gray-500 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2">
              <textarea
                value={newStudentNote}
                onChange={(e) => setNewStudentNote(e.target.value)}
                rows={2}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder={viewMode === 'teacher' ? 'New message to student...' : 'Write a response...'}
              />
              <button
                onClick={addStudentNote}
                className="mt-2 px-3 py-1 bg-primary text-white rounded text-sm"
              >
                Add Note
              </button>
            </div>
          </div>
          {/* Private Notes */}
          {viewMode === 'teacher' && (
            <div className="bg-white p-4 rounded-lg shadow group">
              <h3 className="text-lg font-medium mb-2">Private Notes</h3>
              <div className="space-y-2 blur-sm group-hover:blur-none transition-all">
                {privateNotesList.map((note) => (
                  <div key={note.id} className="flex items-start justify-between">
                    {editingPrivateNoteId === note.id ? (
                      <div className="flex-1 space-y-2">
                        <textarea
                          value={editingPrivateText}
                          onChange={(e) => setEditingPrivateText(e.target.value)}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={savePrivateNote}
                            className="px-3 py-1 bg-primary text-white rounded text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingPrivateNoteId(null)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700">{note.text}</p>
                        <div className="flex space-x-2">
                          <button onClick={() => editPrivateNote(note.id)} className="text-gray-500 hover:text-gray-700">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => deletePrivateNote(note.id)} className="text-gray-500 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <textarea
                  value={newPrivateNote}
                  onChange={(e) => setNewPrivateNote(e.target.value)}
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="New private note..."
                />
                <button
                  onClick={addPrivateNote}
                  className="mt-2 px-3 py-1 bg-primary text-white rounded text-sm"
                >
                  Add Note
                </button>
              </div>
            </div>
          )}
          {/* Classroom Notes */}
          {viewMode === 'teacher' && (
            <div className="bg-white p-4 rounded-lg shadow group">
              <h3 className="text-lg font-medium mb-2">Classroom Notes</h3>
              <div className="space-y-2 blur-sm group-hover:blur-none transition-all">
                {classroomNotesList.map((note) => (
                  <div key={note.id} className="flex items-start justify-between">
                    {editingClassroomNoteId === note.id ? (
                      <div className="flex-1 space-y-2">
                        <textarea
                          value={editingClassroomText}
                          onChange={(e) => setEditingClassroomText(e.target.value)}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={saveClassroomNote}
                            className="px-3 py-1 bg-primary text-white rounded text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingClassroomNoteId(null)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700">{note.text}</p>
                        <div className="flex space-x-2">
                          <button onClick={() => editClassroomNote(note.id)} className="text-gray-500 hover:text-gray-700">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => deleteClassroomNote(note.id)} className="text-gray-500 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <textarea
                  value={newClassroomNote}
                  onChange={(e) => setNewClassroomNote(e.target.value)}
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="New classroom note..."
                />
                <button
                  onClick={addClassroomNote}
                  className="mt-2 px-3 py-1 bg-primary text-white rounded text-sm"
                >
                  Add Note
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Core widgets grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Growth Sparkline */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-700">Growth Sparkline</h2>
            <InfoTooltip content="This sparkline shows the student's marks over their last 6 submissions. Peaks represent improvement, while dips may indicate areas needing attention." />
          </div>
          <div className="mt-4 h-24">
            <Line data={sparklineData} options={sparklineOptions} />
          </div>
        </div>

        {/* Strength Flower */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-700">Strength Flower</h2>
            <InfoTooltip content="This radar chart represents strengths across Engagement, Collaboration, Self-regulation, and Reflection. Longer petals indicate stronger performance in each area." />
          </div>
          <div className="mt-4 h-48">
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        {/* Heat-band Timeline */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-700">Heat-band Timeline</h2>
            <InfoTooltip content="This heat-band timeline visualizes weekly engagement intensity on a 1–5 scale. Lighter bands indicate lower activity; darker bands indicate higher engagement." />
          </div>
          <div className="mt-4 flex h-4 space-x-1">
            {heatData.map((val, idx) => (
              <div
                key={idx}
                className={`${getHeatColor(val)} flex-1`}
                title={`Week ${idx + 1}: intensity ${val}`}
              />
            ))}
          </div>
        </div>

        {/* Positive Feed */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-700">Positive Feed</h2>
            <InfoTooltip content="Here you can see recent achievements and positive highlights. Dismiss them to clear entries as needed." />
          </div>
          <div className="mt-4 space-y-2">
            {achievements.map((ach) => (
              <div
                key={ach.id}
                className="flex items-center justify-between bg-surface-muted p-3 rounded"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{ach.emoji}</span>
                  <span className="text-sm text-gray-700">{ach.text}</span>
                </div>
                <button
                  onClick={() => removeAchievement(ach.id)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Dismiss achievement"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}